import React, { useEffect, useState } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import axios from "axios";
import { EmpresaFNombre, EmpresaFUsuario } from "../../../API/apiurls";

export function EmpresasModel({ mostrar, cerrar, guardar, editar, datosaeditar, title }) {
  const [formData, setFormData] = useState({
    id: null,
    nombre: "",
    usuario: "",
    password: "",
    moduloGeocercas: true,
    moduloPausas: false,
    moduloCinturones: false,
  });

  // Guardamos valores originales para saber si el usuario cambió algo al editar
  const [original, setOriginal] = useState({ nombre: "", usuario: "" });

  const [disableGuardar, setDisableGuardar] = useState(false);
  const [nombreEnUso, setNombreEnUso] = useState(false);
  const [usuarioEnUso, setUsuarioEnUso] = useState(false);

  // Cargar datos cuando se edita / limpiar cuando es nuevo
  useEffect(() => {
    if (datosaeditar) {
      const nombre = datosaeditar.nombre ?? "";
      const usuario = datosaeditar.usuario ?? "";

      setFormData({
        id: datosaeditar.id ?? null,
        nombre,
        usuario,
        password: datosaeditar.password ?? "",
        moduloGeocercas: datosaeditar.moduloGeocercas ?? true,
        moduloPausas: datosaeditar.moduloPausas ?? false,
        moduloCinturones: datosaeditar.moduloCinturones ?? false,
      });

      setOriginal({ nombre, usuario });
    } else {
      setFormData({ id: null, nombre: "", usuario: "", password: "", moduloGeocercas: true, moduloPausas: false, moduloCinturones: false });
      setOriginal({ nombre: "", usuario: "" });
    }

    // Reset flags cada vez que se abre o cambia el modo
    setNombreEnUso(false);
    setUsuarioEnUso(false);
    setDisableGuardar(false);
  }, [datosaeditar, mostrar]);

  // Validar nombre duplicado (solo si cambió cuando editas)
  useEffect(() => {
    const handleBuscarNombre = async () => {
      if (!formData.nombre) {
        setNombreEnUso(false);
        return;
      }

      // Si estás editando y no cambió el nombre, no validar / no bloquear
      if (datosaeditar && formData.nombre === original.nombre) {
        setNombreEnUso(false);
        return;
      }

      try {
        await axios.get(`${EmpresaFNombre}/${formData.nombre}`);
        // Si devuelve 200, existe alguien con ese nombre
        setNombreEnUso(true);
      } catch {
        setNombreEnUso(false);
      }
    };

    handleBuscarNombre();
  }, [formData.nombre, datosaeditar, original.nombre]);

  // Validar usuario duplicado (solo si cambió cuando editas)
  useEffect(() => {
    const handleBuscarUsuario = async () => {
      if (!formData.usuario) {
        setUsuarioEnUso(false);
        return;
      }

      if (datosaeditar && formData.usuario === original.usuario) {
        setUsuarioEnUso(false);
        return;
      }

      try {
        await axios.get(`${EmpresaFUsuario}/${formData.usuario}`);
        setUsuarioEnUso(true);
      } catch {
        setUsuarioEnUso(false);
      }
    };

    handleBuscarUsuario();
  }, [formData.usuario, datosaeditar, original.usuario]);

  // El botón depende SOLO de los flags actuales
  useEffect(() => {
    setDisableGuardar(nombreEnUso || usuarioEnUso);
  }, [nombreEnUso, usuarioEnUso]);

  const limpiar = () => {
    setFormData({ id: null, nombre: "", usuario: "", password: "", moduloGeocercas: true, moduloPausas: false, moduloCinturones: false });
    setOriginal({ nombre: "", usuario: "" });
    setNombreEnUso(false);
    setUsuarioEnUso(false);
    setDisableGuardar(false);
  };

  const handleClose = () => {
    cerrar();
    limpiar();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    if (datosaeditar) editar(formData);
    else guardar(formData);

    cerrar();
    limpiar();
  };

  return (
    <>
      <Modal show={mostrar} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Ingrese el nombre de la empresa</Form.Label>
            <Form.Control
              type="text"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              className="responsive-modal-input"
            />
            {nombreEnUso && <p style={{ color: "red" }}>Este nombre de empresa ya está en uso</p>}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Ingrese el usuario para la empresa</Form.Label>
            <Form.Control
              type="text"
              name="usuario"
              value={formData.usuario}
              onChange={handleChange}
              className="responsive-modal-input"
            />
            {usuarioEnUso && <p style={{ color: "red" }}>Este usuario ya está en uso</p>}
          </Form.Group>

          <Form.Group>
            <Form.Label>Ingrese la contraseña</Form.Label>
            <Form.Control
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="responsive-modal-input"
            />
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>Módulos activos</Form.Label>
            <div style={{ display: "grid", gap: 10 }}>
              <Form.Check
                type="checkbox"
                label="Geocercas, rutas"
                checked={formData.moduloGeocercas}
                onChange={(e) => setFormData((prev) => ({ ...prev, moduloGeocercas: e.target.checked }))}
              />
              <Form.Check
                type="checkbox"
                label="Pausas activas y somnolencia"
                checked={formData.moduloPausas}
                onChange={(e) => setFormData((prev) => ({ ...prev, moduloPausas: e.target.checked }))}
              />
              <Form.Check
                type="checkbox"
                label="Cinturones de seguridad"
                checked={formData.moduloCinturones}
                onChange={(e) => setFormData((prev) => ({ ...prev, moduloCinturones: e.target.checked }))}
              />
            </div>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={disableGuardar}>
            Guardar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
